import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Activity } from 'lucide-react';
import { api } from '@/lib/api';
import { useSite } from '@/hooks/use-sites';
import { KPICard } from '@/components/common/kpi-card';
import { formatNumber, formatDuration, formatPercent } from '@/lib/format';

type Range = '24h' | '7d' | '30d';

export const Route = createFileRoute('/_authenticated/sites/$siteId/')({
  component: SiteOverview,
});

function SiteOverview() {
  const { siteId } = Route.useParams();
  const [range, setRange] = useState<Range>('7d');
  const { data: siteRes } = useSite(siteId);
  const { data: overview, isLoading } = useQuery({
    queryKey: ['overview', siteId, range],
    queryFn: () => api.sites.overview(siteId, range),
  });

  const site = siteRes?.site;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-up">
      <header className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="mb-1 text-2xl font-semibold tracking-tight">{site?.name ?? 'Site'}</h1>
          <p className="font-mono text-xs text-text-muted">{site?.domain}</p>
          <div className="mt-2 inline-flex items-center gap-2 text-xs text-success">
            <Activity className="size-3" />
            Live ingestion
          </div>
        </div>
        <div className="inline-flex rounded-md border border-border bg-bg-surface p-0.5 gap-0.5">
          {(['24h', '7d', '30d'] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                range === r ? 'bg-bg-surface-3 text-text shadow-[0_0_0_1px_var(--border-strong)]' : 'text-text-muted hover:text-text'
              }`}
            >
              {r === '24h' ? '24 hours' : r === '7d' ? '7 days' : '30 days'}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPICard label="Total sessions" value={isLoading ? '—' : formatNumber(overview?.sessions ?? 0)} />
        <KPICard label="Unique visitors" value={isLoading ? '—' : formatNumber(overview?.unique_visitors ?? 0)} />
        <KPICard label="Avg. duration" value={isLoading ? '—' : formatDuration(overview?.avg_duration_ms ?? 0)} />
        <KPICard label="Bounce rate" value={isLoading ? '—' : formatPercent(overview?.bounce_rate ?? 0)} />
      </div>

      <div className="rounded-lg border border-border bg-bg-surface p-6 text-sm text-text-muted">
        Charts, top pages, and recent sessions land in Phase 5.
      </div>
    </div>
  );
}
